import ProductPartNumbers, { ProductPartNumbersStyles } from "@dillon/Overrides/Components/ProductPartNumbers";
import ProductDetailsProductBadgets from "@dillon/Widgets/ProductBadges/ProductBadgets";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    // Verndale Customization
    wishListFacets: getProductListDataViewProperty(state, "properties")?.wishListFacetsBadgets,
    // End Verndale Customization
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

interface VerndaleCustomization {
    wishListFacetBadge?: InjectableCss;
    wishListFacetsContainer?: GridItemProps;
}

export interface ProductListProductInformationStyles extends VerndaleCustomization {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    wishListFacets,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    // Verndale Customization
    // Filter wishlists for the current product
    let productWishLists = wishListFacets
        ? JSON.parse(wishListFacets)
              .filter((wishlist: any) => wishlist.productId === product.id)
              .map((wishlist: any) => wishlist.wishlists)
        : [];

    if (product.isVariantParent && product.variantTraits?.length) {
        const parsed = JSON.parse(wishListFacets || "[]");
        const flattened: any[] = (Array.isArray(parsed) ? parsed : []).flat();
      
        // ids we consider a match for this product group
        const idsToMatch = new Set<string>([String(product.id)]);
        // if you also have child/variant ids in traitValues, include them:
        for (const v of product.variantTraits ?? []) {
          for (const tv of v?.traitValues ?? []) {
            if (tv?.id) {idsToMatch.add(String(tv.id));}
          }
        }
      
        // keep rows where either productId or productParentId matches one of the ids
        const matchingRows = flattened.filter((row: any) => {
          const pid  = row?.productId ? String(row.productId) : null;
          const ppid = row?.productParentId ? String(row.productParentId) : null;
          return (pid && idsToMatch.has(pid)) || (ppid && idsToMatch.has(ppid));
        });
      
        // collect & dedupe wishlists by id
        const wishlists = matchingRows.flatMap((row: any) => row?.wishlists ?? []);
        productWishLists = Array.from(
          new Map(wishlists.map((w: any) => [String(w?.id), w])).values()
        );
      }
      

    const skuLegacyNumber = product.detail!["sku"];
    // End Verndale Customization

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                        skuLegacyNumber={skuLegacyNumber}
                    />
                </GridItem>
            )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
            {/* Verndale Customization */}
            {!product.isVariantParent && product.variantTraits?.length === 0 && productWishLists.length > 0 && (
                <GridItem {...styles.wishListFacetsContainer}>
                    {productWishLists[0].map((wishlist: any) => {
                        return (
                            <StyledWrapper key={wishlist.id} {...styles.wishListFacetBadge}>
                                <ProductDetailsProductBadgets text={wishlist.name} backgroundColor={wishlist.color} />
                            </StyledWrapper>
                        );
                    })}
                </GridItem>
            )}
            {product.isVariantParent && product.variantTraits?.length && productWishLists.length > 0 && (
                <GridItem {...styles.wishListFacetsContainer}>
                    {productWishLists.map((wishlist: any) => {
                        return (
                            <StyledWrapper key={wishlist.id} {...styles.wishListFacetBadge}>
                                <ProductDetailsProductBadgets text={wishlist.name} backgroundColor={wishlist.color} />
                            </StyledWrapper>
                        );
                    })}
                </GridItem>
            )}
            {/* End Verndale Customization */}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
